import {
  Check,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  PlusCircleIcon,
} from "lucide-react";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { Button } from "@repo/ui/components/Base/Button";
import { Link, useNavigate } from "react-router-dom";
import { urlEncodeJSON } from "@/lib/urlParams";
import { RoleProtected } from "../common/RoleProtected";
import { CreateAgendaItemDialog } from "../agendaItems/CreateAgendaItemDialog";
import { useUserStore } from "@/stores/userStore";
import { useMemo, useState } from "react";
import { cn } from "@repo/shared/utils";
import { format } from "date-fns";
import { useAgendaItemList } from "@/hooks/queryHooks/agendaItemHooks";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";
import { useGetTeam, useTeamList } from "@/hooks/queryHooks/teamHooks";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { sortCompare } from "@repo/lib/sortCompare";

export type DashboardBlockType = {
  id: string;
  visible: boolean;
} & (
  | {
      type: "team";
      team: string;
    }
  | {
      type: "youritems";
    }
);

// Switching Dashboard Block component
export interface DashboardBlockProps {
  blockType: DashboardBlockType;
  tenantId: string;
  index: number;
  count: number;
  editing: boolean;
  onMoveTo?: (position: number) => void;
  onVisibilityChange?: (index: number, visible: boolean) => void;
}

export const DashboardBlock = ({
  blockType,
  tenantId,
  index,
  count,
  editing,
  onMoveTo,
  onVisibilityChange,
}: DashboardBlockProps) => {
  let inner: React.ReactNode = null;

  switch (blockType.type) {
    case "team":
      inner = <TeamBlock tenantId={tenantId} teamId={blockType.team} />;
      break;
    case "youritems":
      inner = <YourRequestsBlock tenantId={tenantId} />;
      break;
  }

  if (editing) {
    inner = (
      <div
        className={cn(
          "relative pointer-events-none",
          blockType.visible ? "opacity-40" : "opacity-10"
        )}
      >
        {inner}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-black p-4 m-2 flex flex-col gap-4 justify-between">
      {inner}
      {editing && (
        <div className="flex flex-row justify-between">
          <Button
            variant="ghost"
            disabled={index === 0}
            className={cn(index === 0 && "!opacity-20")}
            onClick={() => onMoveTo?.(index - 1)}
          >
            <ChevronLeft />
          </Button>
          <Button
            className={
              blockType.visible
                ? "bg-tertiary hover:bg-tertiary/90 active:bg-tertiary"
                : "bg-gray-500 hover:bg-gray-500/90 active:bg-gray-500"
            }
            onClick={() => onVisibilityChange?.(index, !blockType.visible)}
          >
            {blockType.visible ? (
              <>
                <Check className="w-4 mr-2" /> Visible
              </>
            ) : (
              <>
                <EyeOff className="w-4 mr-2" /> Hidden
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            disabled={index === count - 1}
            className={cn(index === count - 1 && "!opacity-20")}
            onClick={() =>
              onMoveTo?.(index + 2 /* +2 since insertion is pre-removal */)
            }
          >
            <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
};

// Dashboard Team Block
export interface TeamBlockProps {
  tenantId: string;
  teamId: string;
}

export const TeamBlock = ({ tenantId, teamId }: TeamBlockProps) => {
  const appPaths = new AppUrlPath(tenantId);
  const {
    user,
    id: userId,
    availableTeams,
    setActiveTeam,
  } = useUserStore((s) => s);
  const navigate = useNavigate();

  const availableTeamIds = useMemo(
    () => availableTeams.map((team) => team.id),
    [availableTeams]
  );

  const { data: team } = useGetTeam(tenantId, teamId);
  const { data: items } = useAgendaItemList(tenantId, "org");
  const { data: meetings } = useMeetingList(tenantId, "org");

  const updateActiveTeam = (filters?: TableLayoutState["columnFilters"]) => {
    if (!team) return;

    if (availableTeamIds.includes(teamId)) {
      // if user is in this team, switch to it
      setActiveTeam(team);
    } else {
      // otherwise, just switch to "full organization" and filter the team column
      setActiveTeam("org");
      if (filters) {
        filters.push({ id: "teamId", value: team.name });
      }
    }
  };

  const teamMeetings = useMemo(() => {
    const now = new Date();

    return (
      meetings
        ?.filter(
          (meeting) => meeting.teamId === teamId && meeting.startTime > now
        )
        .sort((a, b) => sortCompare(a.startTime, b.startTime))
        .slice(0, 3) ?? []
    );
  }, [meetings, teamId]);

  const isEditor =
    team?.editUsers.includes(userId) || team?.adminUsers.includes(userId);

  // count requests that were created by this user, are in this team for approval,
  // and have not been decided on yet (whether on a meeting or not)
  const ownedRequestCount =
    items?.filter(
      (item) =>
        item.createdBy === userId &&
        item.teamId === teamId &&
        item.approver &&
        item.approver.approverType === "team" &&
        item.approver.approverId === teamId &&
        (!item.review?.status ||
          item.review.status === "pending" ||
          item.review.status === "deferred")
    ).length ?? "...";

  // count requests that were created for this team for approval, and that were
  // not added to a meeting agenda yet
  const pendingRequestCount = !isEditor
    ? undefined
    : items?.filter(
        (item) =>
          agendaItemRepo.isPending(item, meetings) &&
          item.teamId === teamId &&
          item.approver &&
          item.approver.approverType === "team" &&
          item.approver.approverId === teamId
      ).length ?? "...";

  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between gap-4 items-center">
        <h4 className="text-xl border-b border-black grow text-ellipsis">
          {team?.name ?? "..."}
        </h4>
        <RoleProtected minRole="submit">
          <CreateAgendaItemDialog
            open={open}
            setOpen={setOpen}
            tenantId={tenantId}
            initialData={{
              approver: {
                approverType: "team",
                approverId: teamId,
              },
            }}
            fullButton
          />
          <Button onClick={() => setOpen(true)}>
            <PlusCircleIcon className="mr-2" /> Request
          </Button>
        </RoleProtected>
      </div>
      <div className="flex flex-row gap-4">
        <div
          onClick={(evt) => {
            evt.preventDefault();

            if (!team || !user) return;

            const columnFilters: TableLayoutState["columnFilters"] = [
              { id: "createdBy", value: `${user.firstName} ${user.lastName}` },
              { id: "reviewStatus", value: "unresolved" },
              { id: "approver", value: { type: "team", name: team.name } },
            ];

            updateActiveTeam(columnFilters);

            const url =
              appPaths.agendaItemsPath +
              "#layout=" +
              urlEncodeJSON({ columnFilters } satisfies TableLayoutState);

            navigate(url);
          }}
          className="cursor-pointer text-center flex flex-col gap-2 rounded-md bg-primary/20 p-2 w-0 grow"
        >
          <h1 className="text-2xl">{ownedRequestCount}</h1>
          <p>Your Active Requests</p>
        </div>
        {pendingRequestCount !== undefined && (
          <div
            onClick={(evt) => {
              evt.preventDefault();

              if (!team) return;

              const columnFilters: TableLayoutState["columnFilters"] = [
                { id: "pending", value: true },
                { id: "approver", value: { type: "team", name: team.name } },
              ];

              updateActiveTeam(columnFilters);

              const url =
                appPaths.agendaItemsPath +
                "#layout=" +
                urlEncodeJSON({ columnFilters } satisfies TableLayoutState);

              navigate(url);
            }}
            className="cursor-pointer text-center flex flex-col gap-2 rounded-md bg-secondary/20 p-2 w-0 grow"
          >
            <h1 className="text-2xl">{pendingRequestCount}</h1>
            <p>Pending Requests</p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <h4 className="text-center text-xl">Upcoming Meetings</h4>
        <table>
          <tbody>
            {teamMeetings?.length ? (
              <>
                {teamMeetings.map((meeting) => (
                  <tr key={meeting.id} className="hover:underline">
                    <td>
                      <Link to={appPaths.getMeetingPath(meeting.id)}>
                        {meeting.name}
                      </Link>
                    </td>
                    <td>
                      <Link to={appPaths.getMeetingPath(meeting.id)}>
                        {meeting.startTime
                          ? meeting.startTime.toDateString()
                          : ""}
                      </Link>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} className="text-center">
                    <p
                      onClick={(evt) => {
                        evt.preventDefault();

                        if (!team) return;

                        const columnFilters: TableLayoutState["columnFilters"] =
                          [
                            {
                              id: "meetingDate",
                              value: [format(new Date(), "yyyy-MM-dd"), ""],
                            },
                          ];

                        updateActiveTeam(columnFilters);

                        const url =
                          appPaths.meetingsPath +
                          "#layout=" +
                          urlEncodeJSON({
                            sorting: [{ id: "meetingDate", desc: false }],
                            columnFilters,
                          } satisfies TableLayoutState);

                        navigate(url);
                      }}
                      className="cursor-pointer hover:underline mt-1 block"
                    >
                      See all meetings &raquo;
                    </p>
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={2} className="text-center">
                  No upcoming meetings
                  <br />
                  for this team
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Dashboard Your Requests Block
export interface YourRequestsBlockProps {
  tenantId: string;
}

export const YourRequestsBlock = ({ tenantId }: YourRequestsBlockProps) => {
  const appPaths = new AppUrlPath(tenantId);
  const {
    setActiveTeam,
    user,
    id: userId,
    hasMinRole,
  } = useUserStore((s) => s);

  const navigate = useNavigate();

  const { data: agendaItems } = useAgendaItemList(tenantId, "org");

  // collect agenda items that were created by this user, require some kind of
  // approval, and that were not decided on yet
  const myAgendaItems = useMemo(
    () =>
      agendaItems
        ?.filter(
          (agendaItem) =>
            agendaItem.createdBy === userId &&
            !agendaItem.template &&
            agendaItem.approver &&
            agendaItem.approver.approverType !== "none" &&
            (!agendaItem.review?.status ||
              agendaItem.review.status === "pending" ||
              agendaItem.review.status === "deferred")
        )
        .sort((a, b) => {
          const dateA = a.requestedApprovalDate?.getTime() ?? null;
          const dateB = b.requestedApprovalDate?.getTime() ?? null;

          if (dateA === dateB) return 0; // a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          if (dateA === null) return -1;
          if (dateB === null) return 1;
          return dateA < dateB ? -1 : 1;
        }),
    [agendaItems, userId]
  );
  const { data: teams } = useTeamList(tenantId);

  const agendaItemCount = myAgendaItems?.length;
  const truncatedAgendaItems = myAgendaItems?.slice(0, 10);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between gap-4 items-center">
        <h4 className="text-xl border-b border-black grow text-ellipsis">
          Your Active Requests
        </h4>
      </div>
      <table>
        <tbody>
          {truncatedAgendaItems?.map((agendaItem) => {
            const agendaTeam = teams?.find(
              (team) => team.id === agendaItem.teamId
            );

            return (
              <tr key={agendaItem.id}>
                <td>
                  <Link
                    to={appPaths.getAgendaItemPath(agendaItem.id)}
                    className="hover:underline inline-block mb-2"
                  >
                    {agendaItem.name}
                  </Link>
                </td>
                <td>
                  {hasMinRole("admin") ? (
                    <Link
                      className="hover:underline inline-block mb-2"
                      to={
                        agendaItem.teamId
                          ? appPaths.getTeamPath(agendaItem.teamId)
                          : ""
                      }
                    >
                      {agendaTeam?.name ?? ""}
                    </Link>
                  ) : (
                    <p className="mb-2">{agendaTeam?.name ?? ""}</p>
                  )}
                </td>
                <td>
                  {agendaItem.requestedApprovalDate?.toDateString() ?? null}
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={3} className="text-center">
              <p
                className="cursor-pointer hover:underline mt-1 block"
                onClick={(evt) => {
                  evt.preventDefault();

                  if (!user) return;

                  setActiveTeam("org");

                  const url =
                    appPaths.agendaItemsPath +
                    "#layout=" +
                    urlEncodeJSON({
                      sorting: [{ id: "requestedApprovalDate", desc: false }],
                      columnFilters: [
                        {
                          id: "createdBy",
                          value: `${user.firstName} ${user.lastName}`,
                        },
                        {
                          id: "reviewStatus",
                          value: "unresolved",
                        },
                      ],
                    } satisfies TableLayoutState);

                  navigate(url);
                }}
              >
                See all {agendaItemCount} items &raquo;
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
